/*
 * URLs for the quest's three screens.
 *
 * The whole quest runs off one bundle, so these are not separate apps; they are
 * separate addresses for the same one. Worth having anyway: the tutorial is
 * something you would want to send someone, and "start a Historic game" is a
 * thing you would want to bookmark rather than reach through two clicks every
 * time.
 *
 * Each route is a real directory with its own index.html, which is how
 * /kinder/about/ already works. That keeps the addresses extensionless without
 * asking nginx for a single-page-app fallback: every path below resolves to a
 * file that exists on disk, so a cold load or a refresh lands on the right
 * screen with no server configuration at all.
 *
 * The quest is served from starguessr.sidequests.space, so its own directory is
 * the document root and no longer appears in any of these — /easygame/, not
 * /starguessr/easygame/. The directory on disk is unchanged; the web server
 * picks it from the Host header. See quests.mjs.
 *
 * What is deliberately *not* in the URL is the game in progress. Rounds are
 * generated fresh from a random seed, so there is nothing at /easygame/ to
 * restore; it means "deal me an Easy game", and a refresh honestly deals
 * another one.
 */

const BASE = '/';

export const ROUTES = {
  start: BASE,
  tutorial: `${BASE}tutorial/`,
  easy: `${BASE}easygame/`,
  hard: `${BASE}hardgame/`
};

/*
 * A game route carries its mode in the query rather than in the path, so the
 * three of them share one page on disk and a cold load still works:
 *
 *   (none)   today's daily game, the one the leaderboard ranks
 *   ?random  freshly random places, for playing on after the daily is done
 *   ?result  the stored result of this browser's daily game
 */
export const MODES = ['daily', 'random', 'result'];

export function pathForView(view, tier, mode = 'daily') {
  if (view === 'tutorial') return ROUTES.tutorial;
  if (view !== 'game') return ROUTES.start;
  const base = tier === 'hard' ? ROUTES.hard : ROUTES.easy;
  return mode === 'daily' ? base : `${base}?${mode}`;
}

export function modeFromSearch(search = '') {
  const params = new URLSearchParams(search);
  return MODES.find((mode) => mode !== 'daily' && params.has(mode)) || 'daily';
}

/*
 * Matched on the last path segment rather than the whole path, so the routes
 * survive being served from somewhere other than the site root, and so no route
 * can accidentally match inside another's name. Trailing slashes are optional
 * coming in and always present going out.
 */
export function viewFromPath(pathname) {
  const segments = String(pathname || '').replace(/\/+$/, '').split('/');
  switch (segments[segments.length - 1]) {
    case 'tutorial':
      return { view: 'tutorial', tier: null };
    case 'hardgame':
      return { view: 'game', tier: 'hard' };
    case 'easygame':
      return { view: 'game', tier: 'easy' };
    default:
      return { view: 'start', tier: null };
  }
}
